import type { FC } from 'react';

import { Box, Divider, DynamicYield, Skeleton, Stack, useDeviceType } from '@packages/shared';
import type { CmsData, CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { Banner, ContentExternal, LazyRender } from '@packages/modules';
import { useConfig } from '@packages/utilities';

import type {
  CategoryNavigationItem,
  SearchApiResponse,
  SearchSeoApiResponse,
} from '../../api/rest/types';
import { ProductListPageLayout } from '../ProductListPageLayout';
import { ProductListBreadcrumbs } from '../ProductListBreadcrumbs';
import { ProductListSeo } from '../ProductListSeo';
import { ProductCategoryNavigationDesktop } from '../ProductCategoryNavigationDesktop';
import { SeoProductCategoryNavigation } from '../SeoProductCategoryNavigation';
import { CategoryFilterNavigationMobile } from '../CategoryFilterNavigationMobile';

export type CategoryPageDefaultContentProps = {
  apiResponse: SearchApiResponse;
  topLevelCategories?: string[];
  bannerData?: CmsDataJsonApi;
  cmsContent?: CmsData;
  seoResponse?: SearchSeoApiResponse;
};

/**
 * CategoryPageDefaultContent component description displayed in storybook
 * */
export const CategoryPageDefaultContent: FC<CategoryPageDefaultContentProps> = ({
  apiResponse,
  topLevelCategories,
  bannerData,
  cmsContent,
  seoResponse,
}) => {
  const { isDesktop } = useDeviceType();
  const { dynamicYieldEnabled } = useConfig();

  const {
    information: { contentInformation },
    result: {
      count: productCount,
      navigation: {
        breadCrumb: breadcrumbs = [],
        breadCrumbSchema = [],
        categoryNavigation = [],
        seoCategoryNavigation,
        root,
      },
    },
  } = apiResponse.searchresult;

  const cmsContentPosition = contentInformation?.contentItems?.[0]?.position;

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;

  const categoryPageNavigationItems = (categoryNavigation as CategoryNavigationItem[]).filter(
    (navigationItem) => navigationItem.isSelected,
  );

  return (
    <ProductListPageLayout
      apiResponse={apiResponse}
      topLevelCategories={topLevelCategories}
      pageType="productlist"
      aboveMainContentComponent={
        <>
          <ProductListBreadcrumbs
            breadcrumbs={breadcrumbs}
            breadCrumbSchema={breadCrumbSchema}
            resultCount={productCount}
          />
          {bannerData && cmsContentPosition !== 'Top' && (
            <Box
              sx={{
                img: {
                  maxWidth: '100%',
                },
              }}
            >
              {dynamicYieldEnabled && (
                <>
                  <DynamicYield
                    dataDyId={`mba_topbanner-services-${isDesktop ? 'desktop' : 'mobile'}`}
                  />
                  <DynamicYield
                    dataDyId={`mba_campaign-banner-${isDesktop ? 'desktop' : 'mobile'}`}
                  />
                </>
              )}
              <Banner bannerData={bannerData} />
            </Box>
          )}
          {cmsContent && cmsContentPosition === 'Top' && (
            <LazyRender fallback={<Skeleton height={200} />}>
              <ContentExternal data={cmsContent} />
            </LazyRender>
          )}
        </>
      }
      belowProductsComponent={
        <>
          {cmsContent && cmsContentPosition === 'Bottom' && <ContentExternal data={cmsContent} />}
          {seoInformationExists && <ProductListSeo data={seoResponse} />}
        </>
      }
      leftSidebarDesktopComponent={
        <Stack spacing={1}>
          <ProductCategoryNavigationDesktop items={categoryNavigation} />
          {seoCategoryNavigation && (
            <>
              <Divider />
              <SeoProductCategoryNavigation seoCategoryNavigation={seoCategoryNavigation} />
            </>
          )}
        </Stack>
      }
      renderCategoryDialogContent={(closeDialog) => (
        <CategoryFilterNavigationMobile
          items={categoryPageNavigationItems}
          root={root}
          onNavigate={closeDialog}
        />
      )}
    />
  );
};
